import React from 'react';
import PropTypes from 'prop-types';

/**
 * The lucide glyphs this settings page draws, as inline SVG.
 *
 * WHY NOT @ant-design/icons: the design is lucide throughout — the markup says
 * so (`class="lucide lucide-pencil"`, `lucide-star`) — and the two families do
 * not merely differ in weight. antd's `EditOutlined` is a pen over a boxed
 * sheet; lucide's `pencil` is a single diagonal stroke. Side by side in one
 * toolbar the substitution is obvious, which is how it was spotted.
 *
 * Drawn at 24x24 with stroke-width 2, round caps and joins — lucide's own
 * geometry — so they sit on the same optical weight as the event glyphs the
 * Spatial View already uses.
 *
 * The rendered SIZE is not set here; it comes from the button (`[&_svg]:size-4`
 * is 16px on the header actions, and the category tabs pass 13px explicitly).
 */
export const Lucide = ({ size, children, ...rest }) => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
    focusable="false"
    {...rest}
  >
    {children}
  </svg>
);
Lucide.propTypes = {
  size: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  children: PropTypes.node
};
Lucide.defaultProps = { size: 16 };

/**
 * lucide `pencil`, CURRENT geometry — the body plus the short ferrule stroke
 * across the top end.
 *
 * The older single-path version (`M17 3a2.828 2.828 0 1 1 4 4L7.5 20.5…`) draws
 * the same outline with no ferrule, so the pencil reads as a plain diagonal
 * sliver. Corrected against the reference 2026-09-02.
 */
export const PencilIcon = (props) => (
  <Lucide {...props}>
    <path d="M21.174 6.812a1 1 0 0 0-3.986-3.987L3.842 16.174a2 2 0 0 0-.502.83l-1.321 4.352a.5.5 0 0 0 .623.622l4.353-1.32a2 2 0 0 0 .83-.497z" />
    <path d="m15 5 4 4" />
  </Lucide>
);

/** lucide `trash-2`. */
export const TrashIcon = (props) => (
  <Lucide {...props}>
    <path d="M3 6h18" />
    <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6" />
    <path d="M8 6V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" />
    <line x1="10" y1="11" x2="10" y2="17" />
    <line x1="14" y1="11" x2="14" y2="17" />
  </Lucide>
);

const STAR_POINTS =
  '12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26';

/** lucide `star`, outline. */
export const StarIcon = (props) => (
  <Lucide {...props}>
    <polygon points={STAR_POINTS} />
  </Lucide>
);

/**
 * lucide `star`, filled — the "this IS the default" state. Filled with
 * currentColor so it takes the button's colour like every other glyph here.
 */
export const StarFilledIcon = (props) => (
  <Lucide {...props} fill="currentColor">
    <polygon points={STAR_POINTS} />
  </Lucide>
);

/** lucide `activity` — the category tabs' activity option. */
export const ActivityIcon = (props) => (
  <Lucide {...props}>
    <path d="M22 12h-4l-3 9L9 3l-3 9H2" />
  </Lucide>
);

/** lucide `credit-card` — the category tabs' transaction option. */
export const CreditCardIcon = (props) => (
  <Lucide {...props}>
    <rect x="2" y="5" width="20" height="14" rx="2" />
    <line x1="2" y1="10" x2="22" y2="10" />
  </Lucide>
);

/**
 * lucide `layout-grid` — the SPATIAL LAYOUT column header.
 *
 * The design puts a small glyph before each of the two column labels; ours had
 * bare text. Four squares reads as "arranged on a surface", which is what the
 * spatial layout is.
 */
export const LayoutGridIcon = (props) => (
  <Lucide {...props}>
    <rect width="7" height="7" x="3" y="3" rx="1" />
    <rect width="7" height="7" x="14" y="3" rx="1" />
    <rect width="7" height="7" x="14" y="14" rx="1" />
    <rect width="7" height="7" x="3" y="14" rx="1" />
  </Lucide>
);

/** lucide `mouse-pointer-click` — the ON CLICK column header. */
export const MousePointerClickIcon = (props) => (
  <Lucide {...props}>
    <path d="m9 9 5 12 1.774-5.226L21 14 9 9z" />
    <path d="m16.071 16.071 4.243 4.243" />
    {/* eslint-disable-next-line max-len */}
    <path d="m7.188 2.239.777 2.897M5.136 7.965l-2.898-.777M13.95 4.05l-2.122 2.122M5.106 13.897 2.984 16.02" />
  </Lucide>
);

/**
 * A FILLED circle-alert, for the cap notice.
 *
 * The design's notice leads with a solid dark disc and a white glyph inside it —
 * not lucide's outlined `circle-alert`, and not antd's blue circled "i" either.
 * Drawn as a filled circle plus a knocked-out bar and dot so the glyph stays
 * white whatever the disc colour is.
 */
export const AlertCircleFilledIcon = (props) => (
  <Lucide {...props} fill="currentColor" stroke="none">
    <circle cx="12" cy="12" r="10" />
    <rect x="11" y="6.5" width="2" height="7" rx="1" fill="#fff" />
    <circle cx="12" cy="16.5" r="1.15" fill="#fff" />
  </Lucide>
);
